import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import OutlinedFlagRoundedIcon from '@mui/icons-material/OutlinedFlagRounded'
import { flagObligationRoutine } from 'features/compliance/ducks/actions'
import { getFlagSubmitting } from 'features/compliance/ducks/selectors'
import SearchableSelect from 'components/SearchableSelect'

const REASONS = [
  { value: 'out_of_date',       label: 'Out of date' },
  { value: 'wrong_cadence',     label: 'Wrong cadence' },
  { value: 'wrong_threshold',   label: 'Wrong threshold' },
  { value: 'no_longer_applies', label: 'No longer applies' },
  { value: 'other',             label: 'Other' },
]

const FlagObligationModal = ({ obligation, bpId, onClose }) => {
  const dispatch = useDispatch()
  const submitting = useSelector(getFlagSubmitting)
  const [reason, setReason] = useState('out_of_date')
  const [note, setNote] = useState('')

  const submit = () => {
    dispatch(flagObligationRoutine({
      librarySlug: obligation.librarySlug,
      buildingPassportId: bpId || null,
      programmeObligationId: obligation.id || null,
      reason,
      note: note.trim() || null,
      onSuccess: onClose,
    }))
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderText>
            <Eyebrow>
              <OutlinedFlagRoundedIcon style={{ fontSize: 14 }} /> Flag as out of date
            </Eyebrow>
            <Title>{obligation?.name}</Title>
          </HeaderText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon /></CloseBtn>
        </Header>

        <Body>
          <Lead>
            Tell us what looks wrong with this obligation. We use these reports to
            re-check the rule against its source and update the library.
          </Lead>

          <Field>
            <FieldLabel htmlFor='flag-reason'>What’s the problem?</FieldLabel>
            <SearchableSelect
              id='flag-reason'
              value={reason}
              onChange={v => setReason(v)}
              options={REASONS}
              ariaLabel='What’s the problem?'
            />
          </Field>

          <Field>
            <FieldLabel htmlFor='flag-note'>Add a note (optional)</FieldLabel>
            <TextArea
              id='flag-note'
              placeholder='e.g. "The cadence changed in the 2025 amendment"'
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={2000}
              rows={4}
            />
          </Field>
        </Body>

        <Footer>
          <Cancel onClick={onClose}>Cancel</Cancel>
          <Submit onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit flag'}
          </Submit>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default FlagObligationModal

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 19, 43, 0.45);
  z-index: 1300;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`

const Panel = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%; max-width: 460px;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 48px rgba(0,0,0,0.18);
`

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid #f4f4f6;
`

const HeaderText = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`

const Eyebrow = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #b00020;
`

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 4px; border-radius: 6px;
  display: inline-flex;
  &:hover { background: #f4f4f6; }
`

const Body = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  display: flex; flex-direction: column; gap: 18px;
`

const Lead = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #4d4d56;
`

const Field = styled.div`
  display: flex; flex-direction: column; gap: 8px;
`

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const TextArea = styled.textarea`
  font: inherit;
  font-size: 13px;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 10px 12px;
  resize: vertical;
  min-height: 80px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #f4f4f6;
`

const Cancel = styled.button`
  font: inherit;
  background: none;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Submit = styled.button`
  font: inherit;
  background: linear-gradient(135deg, #e06a4b 0%, #b00020 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(204, 34, 0, 0.28);
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`
